import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, User, Briefcase, ShieldCheck } from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/mockData";

const LOGINS = [
  { to: "/portal/login", label: "Customer login", desc: "Submit ideas & track status", icon: User },
  { to: "/firm/login", label: "Attorney login", desc: "Manage leads & firm profile", icon: Briefcase },
  { to: "/admin/login", label: "Admin login", desc: "Approvals & operations", icon: ShieldCheck },
];

const NAV = [
  { to: "/attorneys", label: "Find Attorneys" },
  { to: "/concierge", label: "Concierge" },
  { to: "/enterprise", label: "Enterprise" },
  { to: "/attorneys/join", label: "For Attorneys" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-[#E1E8E2]" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-3.5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0E6E5E] to-[#0A2520] flex items-center justify-center flex-shrink-0 shadow-sm ring-1 ring-[#0E6E5E]/20">
            <span className="font-display font-black text-2xl text-[#7CC242] leading-none">U</span>
          </div>
          <div>
            <div className="font-display font-extrabold text-2xl text-[#0A2520] leading-none">{BRAND.name}</div>
            <div className="text-[10px] tracking-[0.18em] text-[#0E6E5E] uppercase mt-1 hidden sm:block font-semibold">Securing Ideas · Nurturing Success</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-[15px] font-semibold whitespace-nowrap transition ${isActive ? "text-[#0E6E5E]" : "text-[#0A2520] hover:text-[#0E6E5E]"}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a href={`tel:${BRAND.phone}`} className="hidden md:flex items-center gap-1.5 text-xs text-[#5A6B66] hover:text-[#0E6E5E] transition">
            <Phone size={13} className="text-[#7CC242]" />
            <span className="font-semibold">{BRAND.phone}</span>
          </a>
          <div className="relative hidden sm:block group" data-testid="login-menu">
            <button className="text-sm font-semibold text-[#0A2520] hover:text-[#0E6E5E] px-2 inline-flex items-center gap-1" data-testid="login-btn">
              Log in <ChevronDown size={14} className="group-hover:rotate-180 transition"/>
            </button>
            <div className="absolute right-0 top-full pt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition">
              <div className="bg-white rounded-2xl shadow-xl border border-[#E1E8E2] p-2">
                {LOGINS.map((l) => (
                  <Link key={l.to} to={l.to} className="flex items-center gap-3 p-3 rounded-xl hover:surface-sage transition" data-testid={`login-${l.label.split(" ")[0].toLowerCase()}`}>
                    <div className="w-9 h-9 rounded-lg surface-sage flex items-center justify-center text-[#0E6E5E] flex-shrink-0"><l.icon size={17}/></div>
                    <div>
                      <div className="text-sm font-semibold text-[#0A2520]">{l.label}</div>
                      <div className="text-xs text-[#5A6B66]">{l.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/submit" className="btn-primary text-sm hidden md:inline-flex whitespace-nowrap" data-testid="header-cta-submit">Start Filing</Link>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E1E8E2] bg-white">
          <div className="max-w-[1400px] mx-auto px-5 py-4 space-y-2">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className="block py-2 text-sm font-semibold text-[#0A2520]">
                {item.label}
              </NavLink>
            ))}
            <Link to="/submit" onClick={() => setOpen(false)} className="btn-primary w-full justify-center mt-3">Start Filing</Link>
            <div className="pt-3 mt-2 border-t border-[#E1E8E2] space-y-1">
              {LOGINS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="flex items-center gap-2.5 py-2 text-sm font-semibold text-[#0A2520]">
                  <l.icon size={16} className="text-[#0E6E5E]"/> {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
